/**
 * The IndexedDB schema (`112-offline-drafts.md` §3). Three stores: drafts, outbox,
 * pendingUploads.
 *
 * IndexedDB, not localStorage, for three independently sufficient reasons (§2):
 * localStorage is SYNCHRONOUS and this is a keystroke path; it caps at ~5MB and a single
 * pasted screenshot blows the entire budget; and it has no transactions. Nothing in
 * localStorage is ever user content.
 *
 * THE RULE THIS FILE EXISTS TO KEEP: nothing in here is ever cleared by an auth path, a
 * logout, a 401, or a version upgrade. There is no `clear()`, and there must not be one.
 * The only way an unsent entry leaves is a human clicking Discard (§5.4).
 */
import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { PageDto, ProseMirrorDoc } from '@/api/endpoints/pages';

/* ── drafts ─────────────────────────────────────────────────────────────────── */

/** The user's current, possibly-unsaved editor content for one page. One row per page. */
export interface DraftRecord {
  /** ULID — the key. A conflict backup is keyed `${pageId}:conflict-backup` (§7.3). */
  pageId: string;
  content: ProseMirrorDoc;
  title: string;
  /**
   * The `updatedAt` the page had on the server when this editing session began.
   * THE field that makes reconciliation possible (§7) — without it we cannot tell an
   * unsaved local edit from a divergence, and would have to guess. We never guess.
   */
  baseUpdatedAt: string;
  /** Date.now() — when this draft was written locally. */
  savedAt: number;
}

/* ── outbox ─────────────────────────────────────────────────────────────────── */

export type OutboxKind =
  | 'page.create'
  | 'page.update'
  | 'page.move'
  | 'page.archive'
  | 'page.restore'
  | 'page.delete'
  | 'trash.empty'
  | 'event.create'
  | 'event.update'
  | 'event.delete'
  | 'event.toggle'
  | 'tag.create'
  | 'tag.update'
  | 'tag.delete'
  | 'category.create'
  | 'category.update'
  | 'category.delete'
  | 'settings.patch'
  | 'conversation.create'
  | 'conversation.update'
  | 'conversation.delete'
  | 'systemPrompt.create'
  | 'systemPrompt.update'
  | 'systemPrompt.delete';

export type OutboxStatus = 'pending' | 'inflight' | 'failed';

export interface OutboxRequest {
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  /** e.g. '/api/pages/01JX…' */
  path: string;
  body?: unknown;
  /** Set for content saves. Becomes `If-Unmodified-Since` (`040-api-contract.md`). */
  ifUnmodifiedSince?: string;
}

export interface OutboxEntry {
  /** Auto-increment — THE FIFO ordering (§5.1). Not a timestamp: no ties, no sort. */
  id: number;
  kind: OutboxKind;
  /** The entity this touches. `(kind, entityId)` is the coalescing key. */
  entityId: string;
  request: OutboxRequest;
  /** A human label for the sync popover: "Sszelvyn the Grey — content". */
  label?: string;
  /** Client ids of `pendingUploads` this entry waits for (§8). M3. */
  waitsOnUploads?: string[];
  status: OutboxStatus;
  attempts: number;
  createdAt: number;
  /** Date.now() + backoff. Indexed. */
  nextAttemptAt: number;
  lastError?: { status: number; code: string; message: string };
  /**
   * The server's copy, stashed from a `412 stale_write` body (§7.4). Kept ON the entry
   * so the recovery banner survives a reload — the entry is durable, an in-memory event
   * is not.
   */
  stashedCurrent?: PageDto;
}

/** What a caller hands to `enqueue()`. The bookkeeping fields are the outbox's own. */
export interface EnqueueInput {
  kind: OutboxKind;
  entityId: string;
  request: OutboxRequest;
  label?: string;
  waitsOnUploads?: string[];
}

/* ── pendingUploads ─────────────────────────────────────────────────────────── */

/**
 * M3 owns the upload choreography (`062-images-assets.md`). The store is declared now
 * because the DB version is part of the on-disk format, and because the autosave's
 * blob-src gate (§8, `hasPendingUploads`) reads it from day one — the gate must exist
 * before the thing it gates, or it will be retrofitted around a save path that already
 * works without it.
 */
export interface PendingUpload {
  /** ULID, generated at paste time — the key. */
  clientId: string;
  pageId: string;
  /** Stored NATIVELY. This is the localStorage-killer (§2). */
  blob: Blob;
  filename: string;
  mimeType: string;
  width: number | null;
  height: number | null;
  status: 'pending' | 'uploading' | 'failed';
  attempts: number;
  nextAttemptAt: number;
  createdAt: number;
  assetId?: string;
  url?: string;
  lastError?: string;
}

interface RnDB extends DBSchema {
  drafts: { key: string; value: DraftRecord };
  outbox: {
    key: number;
    value: OutboxEntry;
    indexes: { 'by-next-attempt': number; 'by-entity': string; 'by-status': string };
  };
  pendingUploads: {
    key: string;
    value: PendingUpload;
    indexes: { 'by-page': string; 'by-status': string };
  };
}

export const DB_NAME = 'roleplay-notebook';
export const DB_VERSION = 1;

let handle: Promise<IDBPDatabase<RnDB>> | null = null;

/**
 * Lazy, not eager: a module-level `openDB()` fires on import, which in Jest means every
 * suite that touches any module in this folder opens a database it may not need — and it
 * fires before `fake-indexeddb` can be installed.
 */
export function db(): Promise<IDBPDatabase<RnDB>> {
  handle ??= openDB<RnDB>(DB_NAME, DB_VERSION, {
    upgrade(d) {
      d.createObjectStore('drafts', { keyPath: 'pageId' });

      // `autoIncrement` is not incidental — the monotonic key IS the FIFO order (§3).
      // Iterating the store in key order iterates it in enqueue order, for free.
      const outbox = d.createObjectStore('outbox', { keyPath: 'id', autoIncrement: true });
      outbox.createIndex('by-next-attempt', 'nextAttemptAt');
      outbox.createIndex('by-entity', 'entityId');
      outbox.createIndex('by-status', 'status');

      const uploads = d.createObjectStore('pendingUploads', { keyPath: 'clientId' });
      uploads.createIndex('by-page', 'pageId');
      uploads.createIndex('by-status', 'status');
    },
  });

  return handle;
}

/** Tests only: drop the memoized handle so a fresh fake-indexeddb can be installed. */
export function resetDbHandleForTests(): void {
  handle = null;
}

/**
 * Ask the browser not to evict us under disk pressure. Best-effort hardening, not the
 * guarantee — the guarantee is the write itself (§3 "Persistence"). May be denied; fine.
 */
export async function requestPersistence(): Promise<void> {
  try {
    await navigator.storage?.persist?.();
  } catch {
    /* Firefox prompts, Safari refuses, neither matters. */
  }
}

/**
 * §8, layer 1. A page whose uploads are still in flight must not be saved — the document
 * would go to the server carrying a `blob:` src, which is meaningless in every browser
 * session but the one that made it, and would be a permanently broken image in the
 * database forever.
 *
 * `failed` rows are excluded deliberately: a permanently-failed upload must not block the
 * page's TEXT from ever saving again. The user's words outrank the picture.
 */
export async function hasPendingUploads(pageId: string): Promise<boolean> {
  const rows = await (await db()).getAllFromIndex('pendingUploads', 'by-page', pageId);
  return rows.some((row) => row.status !== 'failed');
}
